import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { LangContext, useLang } from '../../contexts/LangContext.js';
import {
  LineChart, BarChart, AreaChart, RadarChart, ScatterChart,
  Line, Bar, Area, Radar, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
  ResponsiveContainer, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Cell, ComposedChart,
} from 'recharts';
import { COLORS_DARK, COLORS_LIGHT, mono } from '../shared/theme.js';
import { useTheme } from '../../contexts/ThemeContext.js';
import { PI, PF, ModalSectionTitle } from '../shared/helpers.jsx';
import { DRIVE_MODE_GAIN_TABLE, getModeGainsForUI } from '../../core/control/motor_control.js';
import { DriveGroupContent }  from '../tabs/DriveGroupContent.jsx';
import { MotorGroupContent }  from '../tabs/MotorGroupContent.jsx';
import { BmsGroupContent }    from '../tabs/BmsGroupContent.jsx';
import { CalibGroupContent }  from '../tabs/CalibGroupContent.jsx';

// COLORS: LangContext/ThemeContext 없이도 동작하는 런타임 참조
// V49 메인의 isDark 상태에 따라 동적으로 결정되나,
// 탭 파일은 일단 COLORS_LIGHT를 기본값으로 사용
// (isDark props가 필요하면 추후 ThemeContext로 교체)

// LangContext — V49 메인의 Provider 안에서 렌더되므로 동작함

// ============================================================
// ControlModal.js
// EV Powertrain Simulator — Phase 4 컴포넌트 분리
// 제어 파라미터 모달 (모터, BMS, 드라이브 전략)
// ============================================================

// ── ControlModal ──
function ControlModal({ show, onClose, onApply, paramsA, paramsB, setParamsA, setParamsB,
                        initialGroup, initialSubTab }) {
  const COLORS = useTheme();  // [A14] 외부에서 그룹/탭 지정 가능
  const lang = useLang();
  var __hideY2_ctrl_kw = useState(false); var hideY2_ctrl_kw = __hideY2_ctrl_kw[0]; var set_hideY2_ctrl_kw = __hideY2_ctrl_kw[1];
  const e = (ko, en) => lang === "en" ? en : ko;
  const [editTarget, setEditTarget] = useState("A");
  // [A32] 2-Level 탭: 그룹(group) → 서브탭(subTab)
  const [group,  setGroup]  = useState("drive");   // [A17] 초기 탭: 주행 전략
  const [subTab, setSubTab] = useState("sig_input"); // [A17] 첫 화면: 운전자 입력/신호 처리

  // [A14] 외부에서 initialGroup/initialSubTab이 전달되면 열릴 때마다 해당 탭으로 이동
  const { useEffect } = React;
  useEffect(() => {
    if (show) {
      if (initialGroup) setGroup(initialGroup);
      if (initialSubTab) setSubTab(initialSubTab);
    }
  }, [show, initialGroup, initialSubTab]);
  const [selLutMode,  setSelLutMode]  = useState("NORMAL"); // 트랙션 LUT 모드 선택
  const [selAwdMode,  setSelAwdMode]  = useState("NORMAL"); // AWD 모드별 LUT 선택
  const [selRegenMode,setSelRegenMode]= useState("NORMAL"); // [A07] 회생 맵 모드 선택
  const [regenView,   setRegenView]   = useState("coast");  // 회생 캘리브 서브뷰
  const [navMode,     setNavMode]     = useState("flow");   // [A15] "flow" | "tabs"
  // ── 모드별 PI 편집 상태 ───────────────────────────────────────
  const [piEditMode,  setPiEditMode]  = useState("NORMAL"); // 현재 편집 중인 드라이브 모드
  if (!show) return null;

  const params = editTarget === "A" ? paramsA : paramsB;
  const setParams = editTarget === "A" ? setParamsA : setParamsB;
  const up = (k, v) => setParams(p => ({ ...p, [k]: v }));

  // ── 모드별 PI 게인 헬퍼 ──────────────────────────────────────
  // 특정 모드의 게인을 UI 표시용으로 읽기 (커스텀 > 배율 폴백)
  const getModePIGains = (mode) => getModeGainsForUI(params, mode);

  // 특정 모드의 단일 게인 키를 업데이트
  const upModeGain = (mode, key, value) => {
    setParams(p => {
      const existingModeGains = p.mode_gains || {};
      // 해당 모드에 커스텀값이 없으면 현재 계산값으로 초기화
      const currentModeGains = existingModeGains[mode] || (() => {
        const g = getModeGainsForUI(p, mode);
        return { Kp_id: g.Kp_id, Ki_id: g.Ki_id, Kp_iq: g.Kp_iq, Ki_iq: g.Ki_iq, Kp_spd: g.Kp_spd, Ki_spd: g.Ki_spd };
      })();
      const updatedMode = { ...currentModeGains, [key]: value };
      const newModeGains = { ...existingModeGains, [mode]: updatedMode };
      // NORMAL 모드 변경 시 베이스 params도 동기화 (하위 호환성)
      const syncBase = mode === 'NORMAL' ? { [key]: value } : {};
      return { ...p, mode_gains: newModeGains, ...syncBase };
    });
  };

  // 특정 모드를 배율 기본값으로 초기화
  const resetModeToDefault = (mode) => {
    setParams(p => {
      const f = DRIVE_MODE_GAIN_TABLE[mode] || DRIVE_MODE_GAIN_TABLE.NORMAL;
      const defaultGains = {
        Kp_id:  +(p.Kp_id  * f.Kp_id ).toFixed(4),
        Ki_id:  +(p.Ki_id  * f.Ki_id ).toFixed(3),
        Kp_iq:  +(p.Kp_iq  * f.Kp_iq ).toFixed(4),
        Ki_iq:  +(p.Ki_iq  * f.Ki_iq ).toFixed(3),
        Kp_spd: +(p.Kp_spd * f.Kp_spd).toFixed(3),
        Ki_spd: +(p.Ki_spd * f.Ki_spd).toFixed(2),
      };
      const existingModeGains = p.mode_gains || {};
      return { ...p, mode_gains: { ...existingModeGains, [mode]: defaultGains } };
    });
  };

  // NORMAL 기준으로 모든 모드 일괄 재계산
  const resetAllModesToDefault = () => {
    setParams(p => {
      const newModeGains = {};
      ['ECO', 'NORMAL', 'SPORT', 'SNOW', 'OFF_ROAD'].forEach(mode => {
        const f = DRIVE_MODE_GAIN_TABLE[mode];
        newModeGains[mode] = {
          Kp_id:  +(p.Kp_id  * f.Kp_id ).toFixed(4),
          Ki_id:  +(p.Ki_id  * f.Ki_id ).toFixed(3),
          Kp_iq:  +(p.Kp_iq  * f.Kp_iq ).toFixed(4),
          Ki_iq:  +(p.Ki_iq  * f.Ki_iq ).toFixed(3),
          Kp_spd: +(p.Kp_spd * f.Kp_spd).toFixed(3),
          Ki_spd: +(p.Ki_spd * f.Ki_spd).toFixed(2),
        };
      });
      return { ...p, mode_gains: newModeGains };
    });
  };

  // [A32] 4개 그룹 + 각 그룹별 서브탭
  // [A17] CTRL_GROUPS — 1블럭 1탭 원칙, 탭 번호 제거
  // drive:  sig_input(新)→drv_mode→traction_lut→arbitration→awd_calib
  //         regen_map → calib 그룹으로 이동 (상세 LUT 보정성격)
  // motor:  torque_ref→speed→fw_mtpa→current→pwm (변경 없음)
  // bms:    bms_cell→bms_soc→kalman_ext→bms_volt→bms_output(新)
  // calib:  correlation→tire_lateral
  const CTRL_GROUPS = [
    {
      id: "drive", icon: "🎮", label: e("주행 전략","Driving Strategy"), color: "#22c55e",
      tabs: [
        { id: "sig_input",      label: e("운전자 입력 / 신호 처리","Driver Input / Signal"), icon: "🧑‍✈️", color: "#64748b" },
        { id: "drv_mode",       label: e("드라이브 모드","Drive Mode"),           icon: "🎛️", color: "#22c55e" },
        { id: "traction_lut",   label: e("토크 맵 LUT","Torque Map LUT"),        icon: "📊", color: "#f59e0b" },
        { id: "arbitration",    label: e("중재 / 토크 지령","Arbitration / T_req"), icon: "⚖️", color: "#f97316" },
        { id: "friction_brake", label: e("제동 토크 분배","Brake Torque Dist."),  icon: "🛑", color: "#ef4444", isBranch: true },
        { id: "awd_calib",      label: e("AWD / 토크 출력","AWD / Torque Out"),   icon: "🔀", color: "#a855f7" },
      ],
    },
    {
      id: "motor", icon: "⚡", label: e("모터 제어","Motor Control"), color: COLORS.accent,
      tabs: [
        { id: "torque_ref",    label: e("토크 참조 생성","Torque Ref Gen"), icon: "🎯", color: "#22c55e" },
        { id: "speed",         label: e("속도 PI","Speed PI"),              icon: "🔄", color: COLORS.accent2 },
        { id: "fw_mtpa",       label: e("MTPA / MTPV / FW","MTPA / MTPV / FW"), icon: "🧮", color: COLORS.accent5 },
        { id: "current",       label: e("전류 PI","Current PI"),            icon: "🔌", color: COLORS.accent },
        { id: "pwm",           label: e("인버터 / PWM","Inverter / PWM"),   icon: "📶", color: "#ec4899" },
      ],
    },
    {
      id: "bms", icon: "🔋", label: "BMS", color: COLORS.accent3,
      tabs: [
        { id: "bms_cell",     label: e("셀 / 데이터 수집","Cell / Data Acq."),     icon: "🔢", color: COLORS.accent4 },
        { id: "bms_soc",      label: e("SOC 추정","SOC Estimation"),              icon: "🌡️", color: COLORS.thermalOrange },
        { id: "kalman_ext",   label: e("칼만 필터","Kalman Filter"),               icon: "📡", color: "#f59e0b" },
        { id: "bms_volt",     label: e("보호 · 제어 로직","Protection / Control"), icon: "🛡️", color: COLORS.accent3 },
        { id: "bms_output",   label: e("출력 제어","Output Control"),             icon: "🔌", color: "#f97316" },
      ],
    },
    {
      id: "calib", icon: "🔬", label: e("진단/보정","Diagnostics"), color: "#06b6d4",
      tabs: [
        { id: "correlation",  label: e("물리식 보정","Physics Corr."), icon: "🔬", color: "#06b6d4" },
        { id: "tire_lateral", label: e("타이어/횡력","Tyre/Lateral"),  icon: "🛞", color: "#a78bfa" },
      ],
    },
  ];

  const activeGroup = CTRL_GROUPS.find(g => g.id === group) || CTRL_GROUPS[1];
  const activeSubTabs = activeGroup.tabs;

  const handleGroupChange = (gid) => {
    const g = CTRL_GROUPS.find(x => x.id === gid);
    if (g) { setGroup(gid); setSubTab(g.tabs[0].id); }
  };

  // Calculated readouts
  const V_pack = params.bms_v_cell_max * params.bms_cell_count_s;
  const E_cell = params.bms_v_cell_max * params.Q_nom; // Wh approx
  const C_rate_chg = params.bms_i_charge_max / params.Q_nom;
  const C_rate_dis = params.bms_i_discharge_max / params.Q_nom;

  return (
    <div style={modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) { onApply && onApply(); onClose(); } }}>
      <div style={{ ...modalBox, background: COLORS.panel, border: `1px solid ${COLORS.border}`, color: COLORS.text }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${COLORS.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: `linear-gradient(135deg, ${COLORS.panel}, ${COLORS.bg})`, borderRadius: "12px 12px 0 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22 }}>🎛️</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{e("구동 제어 설정","Drive Control Settings")}</div>
            </div>
          </div>
          <button onClick={() => { onApply && onApply(); onClose(); }} style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>

        {/* Vehicle A/B selector */}
        <div style={{ padding: "10px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", gap: 8 }}>
          {["A", "B"].map(t => (
            <button key={t} onClick={() => setEditTarget(t)}
              style={{ flex: 1, padding: "8px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", minWidth: 0,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: editTarget === t ? (t === "A" ? `${COLORS.vehA}20` : `${COLORS.vehB}20`) : "transparent",
                color: editTarget === t ? (t === "A" ? COLORS.vehA : COLORS.vehB) : COLORS.textDim,
                border: `2px solid ${editTarget === t ? (t === "A" ? COLORS.vehA : COLORS.vehB) : COLORS.border}` }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: t === "A" ? COLORS.vehA : COLORS.vehB }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t === "A" ? paramsA.shortName : paramsB.shortName}</span>
            </button>
          ))}
        </div>

        {/* [A15] Compact flow diagram — show/hide toggle */}
        {(() => {
          const DB    = COLORS.isDark;
          const VCU_C = "#22c55e", MCU_C = "#58a6ff", BMS_C = "#a78bfa";
          const dG = group === "drive", mG = group === "motor", bG = group === "bms";
          const nFill = DB ? "#1c2535" : "#e4ecf5";
          const nStr  = DB ? "#4a5568" : "#8fa3bf";
          const nTxt  = DB ? "#8a9ab0" : "#506070";
          const bFill = (c,a) => a ? (DB?`${c}28`:`${c}18`) : (DB?`${c}0d`:`${c}08`);
          const bStr  = (c,a) => a ? c : (DB?`${c}50`:`${c}70`);

          const BH=24, BW=82;
          const TY=14, BY=54;
          const TC=TY+BH/2, BC=BY+BH/2;
          const [DX,VX,MX,IPX]=[4,148,316,490];
          const [DC,VC,MC,IC]=[DX+BW/2,VX+BW/2,MX+BW/2,IPX+BW/2];

          return (
            <div style={{ borderBottom:`1px solid ${COLORS.border}` }}>
              {/* Toggle header */}
              <div style={{
                display:"flex", alignItems:"center", justifyContent:"space-between",
                padding:"3px 14px",
                background: DB ? "#0b1120" : "#e2eaf4",
              }}>
                <span style={{ fontSize:7.5, color:COLORS.textMuted, fontFamily:mono, fontWeight:700, letterSpacing:"0.07em" }}>
                  ⚡ {e("파워트레인 제어 관계도","Powertrain Control Flow")}
                </span>
                <button
                  onClick={()=>setNavMode(v => v==="flow" ? "hide" : "flow")}
                  style={{
                    padding:"1px 8px", borderRadius:4, fontSize:8, cursor:"pointer",
                    background: navMode==="flow" ? (DB?"#1e3a5f":"#dbeafe") : "transparent",
                    color: navMode==="flow" ? (DB?"#60a5fa":"#1d4ed8") : COLORS.textMuted,
                    border:`1px solid ${navMode==="flow" ? (DB?"#3b82f6":"#93c5fd") : COLORS.border}`,
                    fontWeight:600, transition:"all 0.15s",
                  }}>
                  {navMode==="flow" ? e("▲ 접기","▲ Hide") : e("▼ 펼치기","▼ Show")}
                </button>
              </div>

              {/* Diagram body — only when visible */}
              {navMode === "flow" && (
                <div style={{ padding:"4px 14px 5px", background: DB?"#07090f":"#edf2f8", overflowX:"auto" }}>
                  {/* legend */}
                  <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginBottom:2, alignItems:"center", minWidth:754 }}>
                    {[{c:"#ef4444",d:false,l:e("구동","Drive")},{c:BMS_C,d:true,l:e("배터리 제한","Batt")},{c:"#60a5fa",d:true,l:e("피드백","FB")}].map(({c,d,l})=>(
                      <span key={l} style={{ display:"flex", alignItems:"center", gap:2 }}>
                        <svg width="18" height="7" style={{ display:"block", overflow:"visible" }}>
                          <line x1="0" y1="3.5" x2="12" y2="3.5" stroke={c} strokeWidth="1.5" strokeDasharray={d?"3,2":"none"}/>
                          <polygon points="10,1 17,3.5 10,6" fill={c}/>
                        </svg>
                        <span style={{ fontSize:6.5, color:DB?"#4e5f70":"#8898a8" }}>{l}</span>
                      </span>
                    ))}
                  </div>

                  <svg viewBox="0 0 628 86" width="754" height="103" style={{ display:"block", flexShrink:0 }}>
                    <defs>
                      {[{id:"fr",c:"#ef4444"},{id:"fp",c:BMS_C},{id:"fb",c:"#60a5fa"}].map(({id,c})=>(
                        <marker key={id} id={id} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="4" markerHeight="4" orient="auto">
                          <path d="M0,1 L7,4 L0,7 Z" fill={c}/>
                        </marker>
                      ))}
                    </defs>
                    {/* ── Drive arrows (red solid) ── */}
                    <line x1={DX+BW} y1={TC} x2={VX-1} y2={TC} stroke="#ef4444" strokeWidth="1.8" markerEnd="url(#fr)"/>
                    <text x={(DX+BW+VX)/2} y={TC-3} textAnchor="middle" fontSize="7" fill="#ef4444" fontFamily="monospace">{lang==="ko"?"토크 요청 (T_req)":"Torque Request (T_req)"}</text>
                    <line x1={VX+BW} y1={TC} x2={MX-1} y2={TC} stroke="#ef4444" strokeWidth="1.8" markerEnd="url(#fr)"/>
                    <text x={(VX+BW+MX)/2} y={TC-3} textAnchor="middle" fontSize="7" fill="#ef4444" fontFamily="monospace">{lang==="ko"?"토크 명령 (T_cmd)":"Torque Command (T_cmd)"}</text>
                    <line x1={MX+BW} y1={TC} x2={IPX-1} y2={TC} stroke="#ef4444" strokeWidth="1.8" markerEnd="url(#fr)"/>
                    <text x={(MX+BW+IPX)/2} y={TC-3} textAnchor="middle" fontSize="7" fill="#ef4444" fontFamily="monospace">{lang==="ko"?"PWM 구동":"PWM Drive"}</text>
                    {/* ── I_lim: BMS → VCU (vertical, left side of VCU) ── */}
                    <line x1={VC-10} y1={BY-1} x2={VC-10} y2={TY+BH+1} stroke={BMS_C} strokeWidth="1.3" strokeDasharray="4,2" markerEnd="url(#fp)"/>
                    <text x={VC-3} y={(BY+TY+BH)/2+3} fontSize="6.5" fill={BMS_C} fontFamily="monospace">{lang==="ko"?"전류 제한 (I_lim)":"I_lim"}</text>
                    {/* ── Vdc: BMS → MCU (horizontal at BC then up into MCU bottom) ── */}
                    <polyline points={`${VX+BW+2},${BC} ${MX+30},${BC} ${MX+30},${TY+BH+1}`} fill="none" stroke={BMS_C} strokeWidth="1.3" strokeDasharray="4,2" markerEnd="url(#fp)"/>
                    <text x={(VX+BW+2+MX+30)/2} y={BC-5} textAnchor="middle" fontSize="6.5" fill={BMS_C} fontFamily="monospace">{lang==="ko"?"직류전압 (Vdc)":"DC Voltage (Vdc)"}</text>
                    {/* ── ω,I feedback: IPMSM → MCU — 동일선상(y=BC)으로 라우팅 ── */}
                    <polyline points={`${IC},${TY+BH} ${IC},${BC} ${MC+20},${BC} ${MC+20},${TY+BH+1}`} fill="none" stroke="#60a5fa" strokeWidth="1.3" strokeDasharray="3,2" markerEnd="url(#fb)"/>
                    <text x={(IC+MC+20)/2} y={BC-5} textAnchor="middle" fontSize="6.5" fill="#60a5fa" fontFamily="monospace">{lang==="ko"?"속도·전류 피드백 (ω, I)":"Speed & Current Feedback (ω, I)"}</text>
                    <g>
                      <rect x={DX} y={TY} width={BW} height={BH} rx="5" fill={nFill} stroke={nStr} strokeWidth="1.2"/>
                      <text x={DC} y={TY+9} textAnchor="middle" fontSize="9">🧑‍✈️</text>
                      <text x={DC} y={TY+20} textAnchor="middle" fontSize="7.5" fontWeight="600" fill={nTxt} fontFamily="monospace">{e("운전자","Driver")}</text>
                    </g>
                    <g onClick={()=>handleGroupChange("drive")} style={{cursor:"pointer"}}>
                      <rect x={VX} y={TY} width={BW} height={BH} rx="5" fill={bFill(VCU_C,dG)} stroke={bStr(VCU_C,dG)} strokeWidth={dG?2:1.2}/>
                      <text x={VC} y={TY+9} textAnchor="middle" fontSize="9">🎮</text>
                      <text x={VC} y={TY+20} textAnchor="middle" fontSize="7.5" fontWeight="700" fill={VCU_C} fontFamily="monospace">VCU</text>
                      {dG&&<rect x={VX-1} y={TY-1} width={BW+2} height={BH+2} rx="6" fill="none" stroke={VCU_C} strokeWidth="0.8" opacity="0.5"/>}
                      {dG&&<text x={VC} y={TY-2} textAnchor="middle" fontSize="6" fill={VCU_C} fontFamily="monospace">▲</text>}
                    </g>
                    <g onClick={()=>handleGroupChange("motor")} style={{cursor:"pointer"}}>
                      <rect x={MX} y={TY} width={BW} height={BH} rx="5" fill={bFill(MCU_C,mG)} stroke={bStr(MCU_C,mG)} strokeWidth={mG?2:1.2}/>
                      <text x={MC} y={TY+9} textAnchor="middle" fontSize="9">⚡</text>
                      <text x={MC} y={TY+20} textAnchor="middle" fontSize="7.5" fontWeight="700" fill={MCU_C} fontFamily="monospace">MCU</text>
                      {mG&&<rect x={MX-1} y={TY-1} width={BW+2} height={BH+2} rx="6" fill="none" stroke={MCU_C} strokeWidth="0.8" opacity="0.5"/>}
                      {mG&&<text x={MC} y={TY-2} textAnchor="middle" fontSize="6" fill={MCU_C} fontFamily="monospace">▲</text>}
                    </g>
                    <g>
                      <rect x={IPX} y={TY} width={BW} height={BH} rx="5" fill={nFill} stroke={nStr} strokeWidth="1.2"/>
                      <text x={IC} y={TY+9} textAnchor="middle" fontSize="9">🔌</text>
                      <text x={IC} y={TY+20} textAnchor="middle" fontSize="7.5" fontWeight="600" fill={nTxt} fontFamily="monospace">IPMSM</text>
                    </g>
                    <g onClick={()=>handleGroupChange("bms")} style={{cursor:"pointer"}}>
                      <rect x={VX} y={BY} width={BW} height={BH} rx="5" fill={bFill(BMS_C,bG)} stroke={bStr(BMS_C,bG)} strokeWidth={bG?2:1.2}/>
                      <text x={VC} y={BY+9} textAnchor="middle" fontSize="9">🔋</text>
                      <text x={VC} y={BY+20} textAnchor="middle" fontSize="7.5" fontWeight="700" fill={BMS_C} fontFamily="monospace">BMS</text>
                      {bG&&<rect x={VX-1} y={BY-1} width={BW+2} height={BH+2} rx="6" fill="none" stroke={BMS_C} strokeWidth="0.8" opacity="0.5"/>}
                      {bG&&<text x={VC} y={BY-2} textAnchor="middle" fontSize="6" fill={BMS_C} fontFamily="monospace">▲</text>}
                    </g>
                  </svg>
                </div>
              )}
            </div>
          );
        })()}

        {/* [A14] Level-1: 그룹 탭 — 시스템 위치 미니 흐름 표시 */}
        <div style={{ padding: "8px 12px 0", borderBottom: `1px solid ${COLORS.border}`, background: COLORS.panel }}>
          {/* 시스템 간 흐름 미니 표시 */}
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6, fontSize: 8, color: COLORS.textMuted, fontFamily: mono }}>
            {CTRL_GROUPS.filter(g => g.id !== "calib").map((g, i, arr) => (
              <React.Fragment key={g.id}>
                <span style={{
                  color: group === g.id ? g.color : COLORS.textMuted,
                  fontWeight: group === g.id ? 700 : 400,
                  transition: "all 0.15s",
                  opacity: group === g.id ? 1 : 0.5,
                }}>
                  {g.icon} {g.label}
                </span>
                {i < arr.length - 1 && (
                  <span style={{ color: COLORS.border, fontSize: 10 }}>→</span>
                )}
              </React.Fragment>
            ))}
            <span style={{ marginLeft: 4, color: COLORS.border }}>|</span>
            <span style={{ color: group === "calib" ? "#06b6d4" : COLORS.textMuted, opacity: group === "calib" ? 1 : 0.4 }}>
              🔬 {e("진단/보정","Diagnostics")}
            </span>
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 0 }}>
            {CTRL_GROUPS.map(g => (
              <button key={g.id} onClick={() => handleGroupChange(g.id)}
                style={{ padding: "7px 14px", borderRadius: "6px 6px 0 0", fontSize: 11, cursor: "pointer", fontWeight: 700,
                  whiteSpace: "nowrap", transition: "all 0.15s",
                  background: group === g.id ? COLORS.bg : "transparent",
                  color: group === g.id ? g.color : COLORS.textMuted,
                  border: `1px solid ${group === g.id ? COLORS.border : "transparent"}`,
                  borderBottom: group === g.id ? `1px solid ${COLORS.bg}` : "none",
                  marginBottom: group === g.id ? "-1px" : 0 }}>
                {g.icon} {g.label}
              </button>
            ))}
          </div>
        </div>

        {/* [A14] Level-2: 선택된 그룹의 서브탭 — 흐름 순서 번호 + 분기 구분선 */}
        <div style={{ padding: "6px 12px", borderBottom: `1px solid ${COLORS.border}`,
          background: COLORS.bg, display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center",
          borderLeft: `3px solid ${activeGroup.color}` }}>
          {activeSubTabs.map((st, idx) => {
            // Use metadata flags directly from tab definition
            const isBranch   = !!st.isBranch;
            const isAnalysis = !!st.isAnalysis;
            const showSep = isBranch || isAnalysis;
            return (
              <React.Fragment key={st.id}>
                {showSep && (
                  <div style={{ width: 1, height: 22, background: COLORS.border, margin: "0 2px", flexShrink: 0 }} />
                )}
                <button onClick={() => setSubTab(st.redirectTo || st.id)}
                  style={{
                    padding: "4px max(8px, 1.2vw)", borderRadius: 5, fontSize: 10,
                    cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4,
                    background: subTab === st.id ? st.color : "transparent",
                    color: subTab === st.id ? "#000" : COLORS.textDim,
                    border: subTab === st.id ? "none" : `1px solid ${COLORS.border}`,
                    fontWeight: subTab === st.id ? 700 : 400,
                  }}>
                  {st.icon} {st.label}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Content */}
        <div style={{ padding: "16px max(12px, 2vw)", overflowY: "auto", overflowX: "hidden", flex: 1, minWidth: 0 }}>


          {/* ══════════════════════════════════════════════════════════
              [A16] 주행 전략 제어 흐름 블럭도
          ══════════════════════════════════════════════════════════ */}
          {group === "drive" && (() => {
            const mkBlk = (blk, minW=80) => {
              const isActive = blk.tabs && blk.tabs.includes(subTab);
              return (
                <div key={blk.id}
                  onClick={() => blk.tabs && blk.tabs.length > 0 && setSubTab(blk.tabs[0])}
                  style={{ padding:"7px 8px", borderRadius:6, textAlign:"center", minWidth:minW,
                    background: isActive ? `${blk.color}28` : COLORS.bg,
                    border:`2px solid ${isActive ? blk.color : COLORS.border}`,
                    boxShadow: isActive ? `0 0 8px ${blk.color}55` : "none",
                    cursor: blk.tabs && blk.tabs.length > 0 ? "pointer" : "default",
                    transition:"all 0.2s" }}>
                  <div style={{fontSize:14,lineHeight:1,marginBottom:2}}>{blk.icon}</div>
                  <div style={{fontSize:8.5,fontWeight:700,lineHeight:1.3,
                    color:isActive?blk.color:COLORS.text}}>{blk.label}</div>
                  <div style={{fontSize:7,color:COLORS.textMuted,marginTop:2,
                    lineHeight:1.35,whiteSpace:"pre-line"}}>{blk.sub}</div>
                </div>
              );
            };
            const mkArr = (label="", color=COLORS.textMuted, bidir=false) => (
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",
                padding:"0 2px",flexShrink:0,justifyContent:"center"}}>
                <div style={{fontSize:11,color,lineHeight:1}}>{bidir?"↔":"→"}</div>
                {label && <div style={{fontSize:6,color,marginTop:1,textAlign:"center",
                  whiteSpace:"nowrap",maxWidth:56}}>{label}</div>}
              </div>
            );
            const topRow = [
              { id:"drv_in",   icon:"🧑‍✈️", color:"#64748b", tabs:["sig_input"],
                label:e("운전자 입력","Driver Input"),
                sub:e("APP·BPP·ROC\n스티어링 각도","APP·BPP·ROC\nSteering Angle") },
              { id:"sig_proc", icon:"🔧",  color:"#64748b", tabs:["sig_input"],
                label:e("신호 처리","Signal Proc."),
                sub:e("A/D 변환·필터링","A/D Conv.·Filter") },
              { id:"mode_sel", icon:"🎮",  color:"#22c55e", tabs:["drv_mode"],
                label:e("드라이브 모드","Drive Mode"),
                sub:e("Eco/Normal/Sport\n모드별 LUT 선택","Eco/Normal/Sport\nMode LUT Sel.") },
              { id:"lut",      icon:"📊",  color:"#f59e0b", tabs:["traction_lut"],
                label:e("토크 맵 LUT","Torque Map LUT"),
                sub:e("Spd×Pedal 2D LUT\n안전 제한 적용","Spd×Pedal 2D LUT\nSafety Limit") },
              { id:"arb",      icon:"⚖️",  color:"#f97316", tabs:["arbitration"],
                label:e("중재 / T_req","Arbitration"),
                sub:e("BMS 한계·회생 맵\nAWD 분배","BMS Lim·Regen\nAWD Dist.") },
              { id:"t_out",    icon:"📤",  color:"#a855f7", tabs:["awd_calib"],
                label:e("토크 명령 출력","Torque Cmd Out"),
                sub:e("T_req·토크 벡터링\nAWD LUT","T_req·Torque Vec\nAWD LUT") },
              { id:"mcu_ecu",  icon:"🔌",  color:"#60a5fa", tabs:[],
                label:e("MCU/기타 ECU","MCU / ECU"),
                sub:e("CAN/Ethernet 통신","CAN/Ethernet Comm") },
            ];
            const topArrows = [
              {label:"",color:COLORS.textMuted},{label:"",color:COLORS.textMuted},
              {label:e("모드","Mode"),color:"#22c55e"},
              {label:"T_map",color:"#f59e0b"},
              {label:"T_req",color:"#f97316"},
              {label:"CAN",color:"#a855f7"},
            ];
            const feedbacks = [
              { color:"#60a5fa",
                label:e("CAN 피드백","CAN Feedback"),
                desc:e("MCU/ECU → CAN → Arbitration (실시간 조정)","MCU/ECU → CAN → Arbitration (real-time adj.)") },
              { color:"#38bdf8",
                label:e("센서 보정","Sensor Correction"),
                desc:e("센서 → 신호 처리 (오차 보정·VCU 진단)","Sensor → Signal Proc. (error corr./VCU diag.)") },
            ];
            return (
              <div style={{ marginBottom:14, padding:"12px 14px", borderRadius:8,
                background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>
                <div style={{fontSize:10,fontWeight:700,color:"#22c55e",marginBottom:8}}>
                  🎮 {e("주행 전략 제어 흐름 블럭도","Driving Strategy Control Flow")}
                </div>
                <div style={{display:"flex",alignItems:"center",overflowX:"auto",
                  paddingBottom:4,gap:0,minWidth:"max-content"}}>
                  {topRow.map((blk,i) => (
                    <React.Fragment key={blk.id}>
                      {mkBlk(blk)}
                      {i < topRow.length-1 && mkArr(topArrows[i]?.label, topArrows[i]?.color)}
                    </React.Fragment>
                  ))}
                </div>
                <div style={{marginTop:6,borderTop:`1px solid ${COLORS.border}`,
                  paddingTop:6,display:"flex",gap:8,flexWrap:"wrap"}}>
                  {feedbacks.map((fb,i) => (
                    <div key={i} style={{display:"flex",alignItems:"center",gap:5,
                      padding:"3px 10px",borderRadius:4,
                      background:`${fb.color}15`,border:`1px solid ${fb.color}40`}}>
                      <span style={{fontSize:9,color:fb.color}}>↺</span>
                      <span style={{fontSize:9,fontWeight:600,color:fb.color}}>{fb.label}</span>
                      <span style={{fontSize:7.5,color:COLORS.textMuted}}>{fb.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ══════════════════════════════════════════════════════════
              ① 트랙션 토크 맵 LUT
          ══════════════════════════════════════════════════════════ */}
          {/* ══════════════════════════════════════════════════════════
              [A17] 운전자 입력 / 신호 처리 탭 (sig_input)
              블럭도: 🧑‍✈️운전자 입력 + 🔧신호 처리 → 1탭 통합
              [A17] 초기 화면 설정 + 물리식/그래프 연동 완성
          ══════════════════════════════════════════════════════════ */}

          {/* ── 그룹별 탭 콘텐츠 렌더 (분리된 컴포넌트로 위임) ── */}
          {group === "drive" && <DriveGroupContent {...{ params, up, subTab, setSubTab, group, setGroup, COLORS, lang, e, getModePIGains, upModeGain, resetModeToDefault, resetAllModesToDefault, selLutMode, setSelLutMode, selAwdMode, setSelAwdMode, selRegenMode, setSelRegenMode, regenView, setRegenView, piEditMode, setPiEditMode, hideY2_ctrl_kw, set_hideY2_ctrl_kw }} />}
          {group === "motor" && <MotorGroupContent {...{ params, up, subTab, setSubTab, group, setGroup, COLORS, lang, e, getModePIGains, upModeGain, resetModeToDefault, resetAllModesToDefault, selLutMode, setSelLutMode, selAwdMode, setSelAwdMode, selRegenMode, setSelRegenMode, regenView, setRegenView, piEditMode, setPiEditMode, hideY2_ctrl_kw, set_hideY2_ctrl_kw }} />}
          {group === "bms"   && <BmsGroupContent   {...{ params, up, subTab, setSubTab, group, setGroup, COLORS, lang, e, getModePIGains, upModeGain, resetModeToDefault, resetAllModesToDefault, selLutMode, setSelLutMode, selAwdMode, setSelAwdMode, selRegenMode, setSelRegenMode, regenView, setRegenView, piEditMode, setPiEditMode, hideY2_ctrl_kw, set_hideY2_ctrl_kw }} />}
          {group === "calib" && <CalibGroupContent  {...{ params, up, subTab, setSubTab, group, setGroup, COLORS, lang, e, getModePIGains, upModeGain, resetModeToDefault, resetAllModesToDefault, selLutMode, setSelLutMode, selAwdMode, setSelAwdMode, selRegenMode, setSelRegenMode, regenView, setRegenView, piEditMode, setPiEditMode, hideY2_ctrl_kw, set_hideY2_ctrl_kw }} />}

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: `linear-gradient(135deg, ${COLORS.accent5}, #6d28d9)`,
            border: "none", color: "#fff", padding: "8px 24px", borderRadius: 6,
            cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
            {e("✓ 적용","✓ Apply")}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

// --- CSV Download Modal ---

export { ControlModal };